import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useT } from '@/components/visitone/visitone-provider'
import { FullPageStatus } from '@/components/visitone/entry-screen'
import { LoginScreen } from '@/components/auth/login-screen'

/*
 * Gates the authenticated shell. Nothing behind the gate renders until Auth0
 * confirms a valid session, so the shell is unreachable — including by direct
 * URL — without authentication.
 *
 * Unauthenticated users with no callback error are sent straight to Auth0
 * Universal Login (ONC-82 / VIS-319). Credentials are never collected here.
 * A cancelled or failed sign-in returns to a retry surface, not a password
 * field.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const t = useT()
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0()
  const [redirecting, setRedirecting] = React.useState(false)
  const [redirectFailed, setRedirectFailed] = React.useState(false)
  const autoRedirectStarted = React.useRef(false)

  const startLogin = React.useCallback(() => {
    setRedirecting(true)
    setRedirectFailed(false)
    void Promise.resolve(loginWithRedirect()).catch(() => {
      setRedirecting(false)
      setRedirectFailed(true)
    })
  }, [loginWithRedirect])

  React.useEffect(() => {
    if (isLoading || isAuthenticated || error || redirectFailed || autoRedirectStarted.current) {
      return
    }
    autoRedirectStarted.current = true
    startLogin()
  }, [isLoading, isAuthenticated, error, redirectFailed, startLogin])

  if (error || redirectFailed) {
    return (
      <LoginScreen
        onLogin={startLogin}
        busy={redirecting}
        error={error?.message || t('core.auth.errorBody')}
      />
    )
  }

  if (isLoading || redirecting || !isAuthenticated) {
    return <FullPageStatus text={isLoading ? t('core.auth.checking') : t('core.auth.redirecting')} />
  }

  return <>{children}</>
}
